import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import { useNavigate } from "react-router-dom";
import Title from "../components/header/Title";
import UserService from "../services/UserService";
import CategoryService from "../services/CategoryService";
import ProductSkuService from "../services/ProductSkuService";
import OrderService from "../services/OrderService";
import IUser from "../entities/IUser";
import ICategory from "../entities/ICategory";
import IProduct from "../entities/IProduct";
import IProductSku from "../entities/IProductSku";
import { unwrapList, unwrapData, getErrorMessage } from "../services/apiUtils";
import {
  isVariableProductType,
  extractCategoryId,
  buildOptionValueLookup,
  buildSkuCombo,
} from "../services/productTypeUtils";

interface SelectedItem {
  key: string;
  productId: number;
  title: string;
  quantity: number;
  price: number;
  skuId?: number;
  skuCode?: string;
  variantLabel?: string;
}

const CreateOrder: React.FC = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState<IUser[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loadingRef, setLoadingRef] = useState(true);

  const [selectedUserId, setSelectedUserId] = useState<number | "">("");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<number | "">("");

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | "">("");
  const [categoryProducts, setCategoryProducts] = useState<IProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | "">("");
  const [productSkus, setProductSkus] = useState<IProductSku[]>([]);
  const [skuVariantLabels, setSkuVariantLabels] = useState<Record<number, string>>({});
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [selectedSkuId, setSelectedSkuId] = useState<number | "">("");
  const [quantity, setQuantity] = useState(1);

  const [items, setItems] = useState<SelectedItem[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setLoadingRef(true);
    Promise.all([UserService.getUsers(), CategoryService.getCategory()])
      .then(([usersRes, catsRes]: any[]) => {
        setUsers(unwrapList<IUser>(usersRes));
        setCategories(unwrapList<ICategory>(catsRes));
      })
      .catch((err: any) => {
        setError(getErrorMessage(err, "Could not load customers/categories."));
      })
      .finally(() => setLoadingRef(false));
  }, []);

  const handleUserChange = (userId: number | "") => {
    setSelectedUserId(userId);
    setSelectedAddressId("");
    setSelectedUser(null);
    if (userId === "") return;
    setLoadingUser(true);
    UserService.getUserById(Number(userId))
      .then((res: any) => {
        const data = unwrapData(res) as IUser;
        setSelectedUser(data);
        const defaultAddr = data?.addressList?.find((a) => a.isDefaultAddress) || data?.addressList?.[0];
        if (defaultAddr) setSelectedAddressId(defaultAddr.addressId);
      })
      .catch((err: any) => {
        setError(getErrorMessage(err, "Could not load this customer's details."));
      })
      .finally(() => setLoadingUser(false));
  };

  const handleCategoryChange = (categoryId: number | "") => {
    setSelectedCategoryId(categoryId);
    setSelectedProductId("");
    setProductSkus([]);
    setSelectedSkuId("");
    setCategoryProducts([]);
    if (categoryId === "") return;

    const cat = categories.find((c) => c.categoryId === categoryId);
    const products = Array.isArray(cat?.productList) ? (cat!.productList as IProduct[]) : [];
    setCategoryProducts(products.filter((p: any) => p.estatusId !== 3));
  };

  const selectedProduct = useMemo(
    () => categoryProducts.find((p) => p.productId === selectedProductId),
    [categoryProducts, selectedProductId]
  );

  const handleProductChange = (productId: number | "") => {
    setSelectedProductId(productId);
    setSelectedSkuId("");
    setProductSkus([]);
    setSkuVariantLabels({});
    if (productId === "") return;
    const prod = categoryProducts.find((p) => p.productId === productId);
    if (prod && isVariableProductType(prod.product_type)) {
      setLoadingSkus(true);
      const categoryId = extractCategoryId(prod) || Number(selectedCategoryId);
      Promise.all([
        ProductSkuService.getSkusByProductId(Number(productId)),
        ProductSkuService.getProductWithCategoryVariant(categoryId, Number(productId)),
      ])
        .then(([skus, variantRes]) => {
          setProductSkus(skus);
          const body = unwrapData(variantRes as any);
          const categoryVariantSection: Record<string, Array<any>> = body?.["category-variant"] || {};
          const optionValueLookup = buildOptionValueLookup(categoryVariantSection);
          const labels: Record<number, string> = {};
          skus.forEach((s) => {
            labels[s.skuId] = buildSkuCombo(s, optionValueLookup);
          });
          setSkuVariantLabels(labels);
        })
        .catch(() => {
          setProductSkus([]);
          setSkuVariantLabels({});
        })
        .finally(() => setLoadingSkus(false));
    }
  };

  const selectedSku = useMemo(
    () => productSkus.find((s) => s.skuId === selectedSkuId),
    [productSkus, selectedSkuId]
  );

  const requiresSku = selectedProduct ? isVariableProductType(selectedProduct.product_type) : false;

  const handleAddToOrder = () => {
    setError(null);
    if (!selectedProduct) {
      setError("Please select a product.");
      return;
    }
    if (requiresSku && productSkus.length > 0 && !selectedSku) {
      setError("Please select a variant (SKU) for this product.");
      return;
    }
    if (requiresSku && selectedSku && Number(selectedSku.stock) < quantity) {
      setError(`Only ${selectedSku.stock} of this variant are in stock.`);
      return;
    }

    const price = selectedSku ? Number(selectedSku.price) : Number(selectedProduct.price);
    const skuId = selectedSku?.skuId;
    const key = `${selectedProduct.productId}-${skuId ?? "base"}`;

    setItems((prev) => {
      const idx = prev.findIndex((it) => it.key === key);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          key,
          productId: selectedProduct.productId,
          title: selectedProduct.title,
          quantity,
          price,
          skuId,
          skuCode: selectedSku?.skuCode,
          variantLabel: selectedSku ? skuVariantLabels[selectedSku.skuId] : undefined,
        },
      ];
    });
    setQuantity(1);
  };

  const removeItem = (key: string) => {
    setItems((prev) => prev.filter((it) => it.key !== key));
  };

  const orderTotal = items.reduce((sum, it) => sum + it.price * it.quantity, 0);

  const selectedAddress = selectedUser?.addressList?.find((a) => a.addressId === selectedAddressId) || null;

  const canSubmit = !!selectedUserId && !!selectedAddressId && items.length > 0 && !submitting;

  const handleSubmitOrder = () => {
    if (!selectedUser || !selectedAddressId || items.length === 0) {
      setError("Please select a customer, an address, and at least one product.");
      return;
    }
    setError(null);
    setSubmitting(true);

    const now = new Date();

    OrderService.createOrder({
      description: "Backoffice Order",
      orderDate: [now.getFullYear(), now.getMonth() + 1, now.getDate(), now.getHours(), now.getMinutes()],
      userId: Number(selectedUserId),
      orderStatus: 7,
      shipmentDate: null,
      userName: selectedUser.username ?? null,
      userEmail: selectedUser.emailId ?? selectedUser.email ?? null,
      userPhone: selectedUser.mobile ?? selectedUser.phone ?? null,
      userCity: selectedAddress?.cityName || "",
      userAdd: selectedAddress?.address1 || "",
      userCo: selectedAddress?.address2 || "co",
      userFName: selectedUser.firstName ?? null,
      userMName: selectedUser.middleName ?? "",
      userLName: selectedUser.lastName ?? null,
      userMobile: selectedUser.mobile ?? null,
      currency: "pkr",
      shippingMode: "pay by cash",
      customerId: Number(selectedUserId),
      orderOrigin: 1,
      orderDetails: items.map((it) => ({
        productId: it.productId,
        productQty: it.quantity,
        ...(it.skuId ? { skuId: it.skuId } : {}),
      })),
    })
      .then(() => {
        setSnackbar({ open: true, message: "Order placed successfully.", severity: "success" });
        setTimeout(() => navigate("/orders"), 700);
      })
      .catch((err: any) => {
        setError(getErrorMessage(err, "Could not place this order. Please try again."));
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Box>
      <Title title="Create Order" subtitle="Place a new order on behalf of a customer" onBack={() => navigate("/orders")} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loadingRef ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                1. Customer
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="customer-label">Select Customer</InputLabel>
                <Select
                  labelId="customer-label"
                  label="Select Customer"
                  value={selectedUserId}
                  onChange={(e) => handleUserChange(e.target.value as number)}
                >
                  {users.map((u) => (
                    <MenuItem key={u.userId} value={u.userId}>
                      {(u.firstName || u.username || u.emailId || `User #${u.userId}`) +
                        (u.emailId ? ` (${u.emailId})` : "")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loadingUser && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {selectedUser && !loadingUser && (
                <FormControl fullWidth disabled={!selectedUser.addressList?.length}>
                  <InputLabel id="address-label">Delivery Address</InputLabel>
                  <Select
                    labelId="address-label"
                    label="Delivery Address"
                    value={selectedAddressId}
                    onChange={(e) => setSelectedAddressId(e.target.value as number)}
                  >
                    {(selectedUser.addressList || []).map((a) => (
                      <MenuItem key={a.addressId} value={a.addressId}>
                        {[a.address1, a.cityName].filter(Boolean).join(", ")}
                      </MenuItem>
                    ))}
                  </Select>
                  {!selectedUser.addressList?.length && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      This customer has no saved address. Add one before placing an order.
                    </Typography>
                  )}
                </FormControl>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                2. Add Products
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  label="Category"
                  value={selectedCategoryId}
                  onChange={(e) => handleCategoryChange(e.target.value as number)}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.categoryId} value={c.categoryId}>
                      {c.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedCategoryId !== "" && (
                <FormControl fullWidth sx={{ mb: 2 }} disabled={categoryProducts.length === 0}>
                  <InputLabel id="product-label">Product</InputLabel>
                  <Select
                    labelId="product-label"
                    label="Product"
                    value={selectedProductId}
                    onChange={(e) => handleProductChange(e.target.value as number)}
                  >
                    {categoryProducts.map((p) => (
                      <MenuItem key={p.productId} value={p.productId}>
                        {p.title} — Rs. {p.price}
                        {isVariableProductType(p.product_type) ? " (has variants)" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                  {categoryProducts.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      No published products in this category.
                    </Typography>
                  )}
                </FormControl>
              )}

              {loadingSkus && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                  <CircularProgress size={22} />
                </Box>
              )}

              {!loadingSkus && requiresSku && productSkus.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="sku-label">Variant</InputLabel>
                  <Select
                    labelId="sku-label"
                    label="Variant"
                    value={selectedSkuId}
                    onChange={(e) => setSelectedSkuId(e.target.value as number)}
                  >
                    {productSkus.map((s) => (
                      <MenuItem key={s.skuId} value={s.skuId} disabled={Number(s.stock) <= 0}>
                        {skuVariantLabels[s.skuId] || s.skuCode} — Rs. {s.price} ({s.stock} in stock)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <TextField
                  label="Qty"
                  type="number"
                  size="small"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                  inputProps={{ min: 1 }}
                  sx={{ width: 110 }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddShoppingCartRoundedIcon />}
                  disabled={!selectedProductId}
                  onClick={handleAddToOrder}
                >
                  Add to Order
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                3. Order Items
              </Typography>

              {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No items added yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Variant</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((it) => (
                      <TableRow key={it.key}>
                        <TableCell>{it.title}</TableCell>
                        <TableCell>{it.variantLabel || it.skuCode || "-"}</TableCell>
                        <TableCell align="right">Rs. {it.price}</TableCell>
                        <TableCell align="right">{it.quantity}</TableCell>
                        <TableCell align="right">Rs. {it.price * it.quantity}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => removeItem(it.key)}>
                            <DeleteRoundedIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6">Total: Rs. {orderTotal}</Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  disabled={!canSubmit}
                  onClick={handleSubmitOrder}
                >
                  {submitting ? <CircularProgress size={22} color="inherit" /> : "Place Order"}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateOrder;
